"use client";

import {
  forwardRef,
  type ComponentProps,
  type MouseEvent,
  type Ref,
} from "react";

function isPlainPrimaryClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  if (e.defaultPrevented) return false;
  const button = e.button;
  if (button !== undefined && button !== 0) return false;
  const target = e.currentTarget.getAttribute("target");
  if (target && target !== "_self") return false;
  return !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

export type SmoothHashAnchorProps = Omit<ComponentProps<"a">, "href"> & {
  /** In-page fragment only, e.g. `#pricing`. */
  href: string;
};

/**
 * Same-page hash navigation with reliable smooth scrolling. Next.js `Link` and
 * CSS `scroll-behavior` often snap instantly; this uses scrollIntoView instead.
 */
export const SmoothHashAnchor = forwardRef(function SmoothHashAnchor(
  { href, onClick, ...rest }: SmoothHashAnchorProps,
  ref: Ref<HTMLAnchorElement>,
) {
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e);
    if (e.defaultPrevented || !isPlainPrimaryClick(e)) return;
    if (!href.startsWith("#") || href === "#") return;

    const id = href.slice(1);
    const el = document.getElementById(id);
    if (!el) return;

    e.preventDefault();
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    el.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    window.history.pushState(null, "", href);
  }

  return <a ref={ref} href={href} onClick={handleClick} {...rest} />;
});

SmoothHashAnchor.displayName = "SmoothHashAnchor";
